// src/features/files/components/UploadVersionDialog.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Alert,
  Typography,
  Paper,
  LinearProgress,
  TextField,
  Chip,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useFiles, MAX_UPLOAD_BYTES } from '../hooks/useFiles';
import { formatFileSize } from '../utils/formatFileSize';
import { FileDropZone, useFilePicker } from '../upload';

export default function UploadVersionDialog({ open, onClose, file, onSuccess }) {
  const { t } = useTranslation(['files', 'common']);
  const { uploadFileVersion, loading } = useFiles();
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [changeNote, setChangeNote] = useState('');
  const [progress, setProgress] = useState(null);

  const currentVersion = file?.current_version_number ?? 1;
  const nextVersion = currentVersion + 1;

  // Picked or dropped; uploaded on submit, together with the change note
  const handleFileSelect = ([f]) => {
    if (!f) return;
    if (f.size > MAX_UPLOAD_BYTES) {
      setError(t('files:errors.file_too_large'));
      return;
    }
    setSelectedFile(f);
    setError(null);
  };
  const picker = useFilePicker({ onFiles: handleFileSelect, multiple: false });

  const handleSubmit = async () => {
    if (!selectedFile || !file?.file_id) return;
    setError(null);

    setProgress(0);
    const result = await uploadFileVersion(file.file_id, selectedFile, changeNote.trim() || null, {
      onUploadProgress: (event) => event.total && setProgress(Math.round((event.loaded / event.total) * 100)),
    });
    setProgress(null);

    if (result?.success) {
      handleClose();
      onSuccess?.(result.data?.data);
    } else {
      setError(result?.error || t('common:errors.generic'));
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedFile(null);
      setChangeNote('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('files:upload_new_version')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Version indicator */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label={`v${currentVersion}`} size="small" variant="outlined" />
            <ArrowForwardIcon fontSize="small" color="action" />
            <Chip label={`v${nextVersion}`} size="small" color="primary" />
            <Typography variant="body2" color="text.secondary" noWrap sx={{ ml: 0.5 }}>
              {file?.filename}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Change note */}
          <TextField
            label={t('files:labels.change_note')}
            placeholder={t('files:placeholders.change_note')}
            multiline
            minRows={2}
            maxRows={4}
            value={changeNote}
            onChange={(e) => setChangeNote(e.target.value)}
            disabled={loading}
            inputProps={{ maxLength: 2000 }}
          />

          {/* File picker */}
          {picker.input}
          {!selectedFile ? (
            <FileDropZone onFiles={handleFileSelect} multiple={false} disabled={loading}>
              <Box
                component="button"
                type="button"
                onClick={picker.open}
                sx={{
                  width: '100%',
                  p: 4,
                  border: 2,
                  borderStyle: 'dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  color: 'inherit',
                  font: 'inherit',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover, &:focus-visible': { borderColor: 'primary.main', bgcolor: 'action.hover', outline: 'none' },
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" gutterBottom>
                  {t('files:drag_drop_file')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('files:or_click_to_browse')}
                </Typography>
              </Box>
            </FileDropZone>
          ) : (
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FileIcon color="primary" sx={{ fontSize: 40 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" noWrap>{selectedFile.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatFileSize(selectedFile.size)}
                  </Typography>
                </Box>
                {!loading && (
                  <Button size="small" onClick={() => setSelectedFile(null)}>
                    {t('common:buttons.remove')}
                  </Button>
                )}
              </Box>
              {loading && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress variant={progress ? 'determinate' : 'indeterminate'} value={progress ?? 0} />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    {t('files:uploading')}{progress ? ` · ${progress} %` : '…'}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common:buttons.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !selectedFile}
        >
          {loading ? <CircularProgress size={24} /> : t('files:actions.upload_new_version')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
